import * as React from 'react';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {analyticsClient} from '../../../analytics-client';
import {DateRangePicker} from '../../../client_modules/shells/react/components/date-range-picker/date-range-picker';

import {PageContainer, PageTitleBar} from '../../layout';
import {getReportDateRanges} from '../../reports/report-constants';
import type {ReportURLParams} from '../../reports/report-types';
import {DASHBOARD} from '../../reports/report-types';

import {AudiencesCard} from './audiences/audiences-card';
import {MarketingDashboardChecklist} from './checklist/marketing-dashboard-checklist';
import {ConnectedFormsCard} from './connected-forms/connected-forms-card';
import {DripSequencesCard} from './drip-sequences/drip-sequences-card';
import {EditionsCard} from './editions/editions-card';
import {FormsCard} from './forms/forms-card';
import {LandingPagesCard} from './landing-pages/landing-pages-card';
import {VisitorIqCard} from './visitor-iq/visitor-iq-card';
import {WebsiteTrafficCard} from './website-traffic/website-traffic-card';

import styles from './dashboard.module.css';

export const DEFAULT_TIMESPAN = '-d30';

type Props = {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function Dashboard(props: Props) {
    const dateRangeType = 'period';
    const dateRangeFilter = safelyGetFilterByKey(props.activeFilters, dateRangeType);

    const timespan = dateRangeFilter || DEFAULT_TIMESPAN;

    // @ts-expect-error
    const handleDateRangeChange = (newFilter, comparisonType) => {
        props.onParamChange(
            {
                filter: {...props.urlParams.filter, period: newFilter},
                report: {
                    ...(props.urlParams.report || {}),
                    compareTo: comparisonType,
                },
            },
            true
        );

        analyticsClient.track(analyticsClient.EVENTS.TRACK_MARKETING_DASHBOARD_DATE_RANGE_CHANGED);
    };

    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_MARKETING_DASHBOARD_VIEWED, {
            dateRange: timespan,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PageContainer
            title='Dashboard'
            pageTitleBar={
                <PageTitleBar
                    title='Dashboard'
                    subheaderPrimaryText='The all in one dashboard for you and your marketing'
                />
            }
            noPadding={true}
        >
            <div className='pt-16 px-16 flex flex-dir-col border-box full-height'>
                <div className='flex justify-end mb-16'>
                    <DateRangePicker
                        filter={dateRangeFilter}
                        // @ts-expect-error
                        dateRangeOptions={getReportDateRanges(DASHBOARD)}
                        comparisonType={
                            props.urlParams.report ? props.urlParams.report.compareTo : undefined
                        }
                        onChange={handleDateRangeChange}
                        variant='primary'
                    />
                </div>
                <div className='overflow-hidden'>
                    <div className='full-height overflow-auto'>
                        <MarketingDashboardChecklist />
                        <div className='mb-32'>
                            <StackLegacy spacing={16}>
                                <WebsiteTrafficCard
                                    onParamChange={props.onParamChange}
                                    urlParams={props.urlParams}
                                />
                                <EditionsCard timespan={timespan} />
                                <DripSequencesCard timespan={timespan} />
                                <FormsCard
                                    onParamChange={props.onParamChange}
                                    urlParams={props.urlParams}
                                />
                                <LandingPagesCard
                                    onParamChange={props.onParamChange}
                                    urlParams={props.urlParams}
                                />
                                <ConnectedFormsCard
                                    onParamChange={props.onParamChange}
                                    urlParams={props.urlParams}
                                />
                                <div className={styles.row}>
                                    <AudiencesCard timespan={timespan} />
                                    <VisitorIqCard timespan={timespan} />
                                </div>
                            </StackLegacy>
                        </div>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
}
